import { useMemo } from 'react';
import {
  DataTableAction,
  EllipsisMenuButton,
  Menu,
  MenuContent,
  MenuTrigger,
} from '@optiaxiom/react';
import {
  IconAgent,
  IconCopy,
  IconPenToSquare,
  IconRocket,
  IconTrashCan,
} from '@optimizely/axiom-icons';

import { UnifiedAgent } from '@frontend/types/agent';

type ActionsCellProps = {
  agent: UnifiedAgent;
  onCopyAgent: (agent: UnifiedAgent) => void;
  onExportAgent: (agent: UnifiedAgent) => void;
  onEditAgent: (agent: UnifiedAgent) => void;
  onPublishAgent?: (agent: UnifiedAgent) => void;
  onDeleteAgent: (agent: UnifiedAgent) => void;
  showPublishOption: boolean;
};

export const ActionsCell = ({
  agent,
  onCopyAgent,
  onExportAgent,
  onEditAgent,
  onPublishAgent,
  onDeleteAgent,
  showPublishOption,
}: ActionsCellProps) => {
  const menuOptions = useMemo(
    () => [
      {
        label: 'Edit Agent',
        addon: <IconPenToSquare />,
        intent: 'neutral' as const,
        execute: () => onEditAgent(agent),
      },
      {
        label: 'Duplicate Agent',
        addon: <IconAgent />,
        intent: 'neutral' as const,
        execute: () => onCopyAgent(agent),
      },
      {
        label: 'Copy as JSON',
        addon: <IconCopy />,
        intent: 'neutral' as const,
        execute: () => onExportAgent(agent),
      },
      ...(showPublishOption && onPublishAgent
        ? [
            {
              label: 'Publish Agent',
              addon: <IconRocket />,
              intent: 'neutral' as const,
              execute: () => onPublishAgent(agent),
            },
          ]
        : []),
      {
        label: 'Delete Agent',
        addon: <IconTrashCan />,
        intent: 'danger' as const,
        group: {
          label: 'Delete Agent',
          hidden: true,
          separator: true,
        },
        execute: () => onDeleteAgent(agent),
      },
    ],
    [
      agent,
      onCopyAgent,
      onExportAgent,
      onEditAgent,
      onPublishAgent,
      onDeleteAgent,
      showPublishOption,
    ]
  );

  return (
    <Menu options={menuOptions}>
      <DataTableAction asChild visible="always">
        <MenuTrigger asChild>
          <EllipsisMenuButton appearance="subtle" aria-label="Actions" />
        </MenuTrigger>
      </DataTableAction>
      <MenuContent />
    </Menu>
  );
};
